import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { createPopper, Instance as PopperInstance } from '@popperjs/core';
import { Subscription } from 'rxjs';
import { TooltipMenuService } from './tooltip-menu.service';

@Component({
  selector: 'app-tooltip-menu',
  templateUrl: './tooltip-menu.component.html',
  styleUrls: ['./tooltip-menu.component.scss'],
})
export class TooltipMenuComponent implements OnInit, OnDestroy {
  @Output() clickOutside = new EventEmitter<void>();
  @ViewChild('tooltipMenu', { static: true }) tooltipMenu!: ElementRef<HTMLElement>;

  isMenuOpen = false;

  private popperInstance: PopperInstance | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private closeSubscription: Subscription | null = null;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private tooltipMenuService: TooltipMenuService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    // Écoute les notifications de fermeture de tous les tooltips
    this.closeSubscription = this.tooltipMenuService.closeAllTooltips$.subscribe(() => this.close());
  }

  ngOnDestroy(): void {
    this.closeSubscription?.unsubscribe();
    this.destroyPopper();
  }

  toggleMenu(event: MouseEvent, ref: HTMLElement | null = null): void {
    event?.stopPropagation();

    if (this.isMenuOpen) {
      this.close();
      return;
    }

    const target = ref || (event?.target as HTMLElement);
    this.open(target);
  }

  private open(target: HTMLElement): void {
    this.tooltipMenuService.closeAllTooltips();

    this.isMenuOpen = true;

    this.cdr.detectChanges();

    this.createPopper(target);
  }

  private close(): void {
    if (!this.isMenuOpen && !this.popperInstance) {
      return;
    }

    this.isMenuOpen = false;
    this.destroyPopper();
  }

  private createPopper(target: HTMLElement): void {
    this.destroyPopper();

    const menu = this.tooltipMenu.nativeElement;

    this.zone.runOutsideAngular(() => {
      this.popperInstance = createPopper(target, menu, {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
          {
            name: 'flip',
            options: { fallbackPlacements: ['bottom-end', 'top-start', 'top-end'] },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 8,
              altAxis: true,
              tether: false,
            },
          },
        ],
      });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.popperInstance?.update());
        this.resizeObserver.observe(menu);
      }
    });

    requestAnimationFrame(() => this.popperInstance?.update());
  }

  private destroyPopper(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.popperInstance?.destroy();
    this.popperInstance = null;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const clickedElement = event.target as HTMLElement;
    // Vérifie si le clic est en dehors de ce composant

    if (clickedElement.matches('.ng-value-icon') || clickedElement.matches('.ng-star-inserted')) {
      return;
    }

    if (this.isMenuOpen && !this.elementRef.nativeElement.contains(clickedElement)) {
      this.close();

      if (!clickedElement.closest('.tooltip-item')) {
        this.clickOutside.emit();
      }
    }
  }
}
